import type { Run } from '@verifyloop/core';
import { IconActivity } from '@tabler/icons-react';
import { Badge } from './ui';
import { formatDuration, formatRelative } from '../lib/format';

/**
 * Pass-rate trend for a project, drawn from the five most recent runs the server
 * decorates onto the project. One bar per run, oldest on the left, so a
 * regression reads as a colour break rather than as a number someone has to
 * remember. Bar height is normalised to the slowest run in the window.
 */
export function PassRateTrend({ runs }: { runs: readonly Run[] }): JSX.Element {
  const ordered = [...runs].sort((a, b) => a.startedAt.localeCompare(b.startedAt));

  if (ordered.length === 0) {
    return (
      <Badge variant="outline" className="trend trend--empty">
        <IconActivity size={13} stroke={1.7} aria-hidden="true" />
        no runs yet
      </Badge>
    );
  }

  const slowest = Math.max(...ordered.map((run) => run.durationMs ?? 0), 1);
  const decided = ordered.filter((run) => (run.verdict ?? run.status) === 'passed' || (run.verdict ?? run.status) === 'failed');
  const passed = decided.filter((run) => (run.verdict ?? run.status) === 'passed').length;
  const rate = decided.length === 0 ? null : passed / decided.length;

  return (
    <div
      className="trend"
      role="img"
      aria-label={
        rate === null
          ? `${ordered.length} recent run(s), none decided yet`
          : `${ordered.length} recent run(s), pass rate ${Math.round(rate * 100)}%`
      }
    >
      <div className="trend__bars">
        {ordered.map((run) => {
          const height = Math.max(8, Math.round(((run.durationMs ?? 0) / slowest) * 100));
          return (
            <span
              key={run.id}
              className={`trend__bar trend__bar--${run.status}`}
              style={{ height: `${height}%` }}
              title={`${run.status} · ${formatRelative(run.startedAt)} · ${formatDuration(run.durationMs)}${run.failureKind ? ` · ${run.failureKind}` : ''}`}
            />
          );
        })}
      </div>
      <span className="trend__rate num" title="pass rate over the recent runs on this project">
        {rate === null ? '-' : `${Math.round(rate * 100)}%`}
      </span>
    </div>
  );
}

/** A slimmer variant for a table cell. */
export function PassRateCell({ rate, total }: { rate: number | null; total: number }): JSX.Element {
  if (rate === null) return <span className="muted">-</span>;
  const tone = rate >= 0.95 ? 'pass' : rate >= 0.6 ? 'warn' : 'fail';
  return (
    <span className={`rate rate--${tone}`} title={`${total} run(s) recorded`}>
      <span className="rate__track" aria-hidden="true">
        <span className="rate__fill" style={{ width: `${Math.round(rate * 100)}%` }} />
      </span>
      <span className="num">{Math.round(rate * 100)}%</span>
    </span>
  );
}